export type AccessLevel = "view" | "edit";

export type ShareKind = "division" | "team";

/** Minimal shape of a Division/Team as nested on a record response. */
export interface NamedUnit {
  id: number;
  display_name: string;
}

/** One additional Division or Team a record is shared into. */
export interface Share {
  kind: ShareKind;
  id: number;
  name: string;
  /**
   * Access level of the share. Optional because record responses don't carry
   * it yet (`additional_divisions`/`additional_teams` are plain lists); rows
   * without a level render no level pill.
   */
  level?: AccessLevel;
}

export interface NewShare {
  kind: ShareKind;
  id: number;
  /** Only set when the panel's `supportsLevels` is on. */
  level?: AccessLevel;
}

/** A record's shares as the write payload expects: full id lists per kind. */
export const shareIdLists = (shares: Share[]) => ({
  additional_divisions: shares
    .filter((s) => s.kind === "division")
    .map((s) => s.id),
  additional_teams: shares.filter((s) => s.kind === "team").map((s) => s.id),
});

export const LEVEL_LABEL: Record<AccessLevel, string> = {
  view: "View only",
  edit: "Can edit",
};

export const KIND_LABEL: Record<ShareKind, string> = {
  division: "Division",
  team: "Team",
};

/** Flattens a record's additional Divisions/Teams into one "Shared with" list. */
export const buildShares = (
  additionalDivisions?: NamedUnit[] | null,
  additionalTeams?: NamedUnit[] | null,
): Share[] => [
  ...(additionalDivisions ?? []).map((d) => ({
    kind: "division" as const,
    id: d.id,
    name: d.display_name,
  })),
  ...(additionalTeams ?? []).map((t) => ({
    kind: "team" as const,
    id: t.id,
    name: t.display_name,
  })),
];
